/**
 * Memoized `normalizeXtsConfig` pass with onError surfacing.
 *
 * Runs the normalization during render itself (useMemo) so errors are
 * captured into a ref and reported via a separate effect — collapses
 * the legacy "useState + useEffect" double-render into one.
 */

import { useEffect, useMemo, useRef } from 'react';
import {
  normalizeXtsConfig,
  type NormalizedXtsConfig,
} from '../../geometry/normalizeXtsConfig.js';
import {
  XtsViewerErrorException,
  type XtsConfig,
  type XtsViewerError,
} from '../../types.js';

export function useNormalizedConfig(
  config: XtsConfig,
  onError?: (err: XtsViewerError) => void,
): NormalizedXtsConfig | null {
  const errorRef = useRef<XtsViewerError | null>(null);

  const normalized = useMemo<NormalizedXtsConfig | null>(() => {
    try {
      errorRef.current = null;
      return normalizeXtsConfig(config);
    } catch (err) {
      errorRef.current =
        err instanceof XtsViewerErrorException
          ? { code: err.code, message: err.message, details: err.details }
          : {
              code: 'invalid-config',
              message: (err as Error)?.message ?? String(err),
              details: err,
            };
      return null;
    }
  }, [config]);

  useEffect(() => {
    if (errorRef.current && onError) {
      onError(errorRef.current);
    }
  }, [normalized, onError]);

  return normalized;
}
